import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PermitStep, PersonRole } from "@ha/data/basic";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";

import { LanguageStateSelectors } from "@ha/ui/common";
import { PermitPhotoActions } from "../../../permit-create/states/permit-photo.actions";
import { PermitUserActions } from "../../../shared/states/permit-user.action";
import { DetailsTab } from "../../models/details-tab.enum";
import { PermitBasicDetailsDto } from "../../models/permit-basic-details.model";
import { PermitDetailsActions } from "../../states/permit-details.action";
import { PermitDetailsSelectors } from "../../states/permit-details.selectors";

@UntilDestroy()
@Component({
    selector: "pmt-permit-details",
    templateUrl: "permit-details.component.html",
    styleUrls: ["permit-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitDetailsComponent implements OnInit {
    @Input()
    public permitId: number;

    @Select(PermitDetailsSelectors.currentUserRoles)
    public currentUserRoles$: Observable<PersonRole[]>;

    @Select(PermitDetailsSelectors.showPermitStarted)
    public showPermitStarted$: Observable<boolean>;

    @Select(PermitDetailsSelectors.showFlammableAtexPermitCreated)
    public showFlammableAtexPermitCreated$: Observable<boolean>;

    @Select(PermitDetailsSelectors.basicDetails)
    public details$: Observable<PermitBasicDetailsDto>;

    @Select(LanguageStateSelectors.currentLanguage)
    public currentLanguage$: Observable<string>;

    public PermitStep = PermitStep;
    public DetailsTab = DetailsTab;
    public currentTab = DetailsTab.summary;

    constructor(private store: Store, private route: ActivatedRoute, private actions$: Actions) {}

    public ngOnInit(): void {
        this.store.dispatch(new PermitUserActions.GetCurrentPerson());
        this.store.dispatch(new PermitDetailsActions.GetBasicDetails(this.permitId));
        this.store.dispatch(new PermitDetailsActions.GetCurrentUserRoles(this.permitId));

        this.actions$.pipe(ofActionSuccessful(PermitDetailsActions.GetBasicDetails), untilDestroyed(this))
            .subscribe(() => {
                const tab = this.route.snapshot.queryParamMap.get("tab");
                if (tab === "complete") {
                    this.currentTab = DetailsTab.complete;
                }
            });

        this.currentLanguage$.pipe(untilDestroyed(this)).subscribe((language) => {
            this.store.dispatch(new PermitDetailsActions.GetComments(this.permitId, language));
            this.store.dispatch(new PermitPhotoActions.GetPermitPhotos(language, true));
        });
    }

    public getMenuItemVisibility(tab: DetailsTab, userRoles: PersonRole[], details: PermitBasicDetailsDto): boolean {
        switch (tab) {
            case DetailsTab.summary:
                return true;
            case DetailsTab.team:
                return userRoles?.some((_) =>
                    _ === PersonRole.PermitIssuer
                    || _ === PersonRole.Superintendent
                    || _ === PersonRole.Observer
                    || _ === PersonRole.HotWorker
                    || _ === PersonRole.FireWatcher
                    || _ === PersonRole.PostFireWatcher
                );
            case DetailsTab.comments:
                return true;
            case DetailsTab.photos:
                return true;
            case DetailsTab.notifications:
                return userRoles?.some((_) =>
                    _ === PersonRole.PermitIssuer
                    || _ === PersonRole.Observer
                );
            case DetailsTab.logs:
                return userRoles?.some((_) =>
                    _ === PersonRole.PermitIssuer
                    || _ === PersonRole.Observer
                );
            case DetailsTab.complete:
                return userRoles?.some((_) =>
                    _ === PersonRole.PermitIssuer
                    || (_ === PersonRole.PostFireWatcher && details.endPostFireWatchSignatureRequired === true)
                );
            default:
                return false;
        }
    }

    public isCurrentTab(tab: DetailsTab): boolean {
        return this.currentTab === tab;
    }

    public setTab(tab: DetailsTab): void {
        this.currentTab = tab;
    }
}
